import { useEffect } from 'react';

import { Box, Typography } from '@/shared/ui';

import { useFactsheetDetails } from '@/features/factsheet/api/queries';
import Loader from '@/shared/ui/feedback/Spinner';

import FactsheetBody from './FactsheetBody';
import FactsheetHeader from './FactsheetHeader';

interface FactsheetWindowProps {
  factsheetKey: string;
  openNewTab: (code: string) => void;
  updateTabName: (code: string, newName: string) => void;
}

const ErrorState = () => (
  <Box
    sx={{
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      height: '100%'
    }}
  >
    <Typography sx={{ fontSize: 30, fontWeight: 700, mb: 1, mt: 3 }}>
      Factsheet not found
    </Typography>
    <Typography sx={{ fontSize: 24 }}>We couldn't find a factsheet with that code.</Typography>
  </Box>
);

const LoadingState = () => (
  <Box
    sx={{
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%'
    }}
  >
    <Loader />
  </Box>
);

const FactsheetWindow = ({ factsheetKey, openNewTab, updateTabName }: FactsheetWindowProps) => {
  const { data: factsheetDetails, isLoading, isError } = useFactsheetDetails(factsheetKey);

  // Update tab name when data is fetched successfully
  useEffect(() => {
    if (factsheetDetails) {
      updateTabName(factsheetKey, factsheetDetails.code);
    }
  }, [factsheetDetails, factsheetKey, updateTabName]);

  // Update tab name on error
  useEffect(() => {
    if (isError) {
      updateTabName(factsheetKey, factsheetKey);
    }
  }, [isError, factsheetKey, updateTabName]);

  if (isError) {
    return <ErrorState />;
  }

  if (isLoading || !factsheetDetails) {
    return <LoadingState />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexGrow: 1,
        overflow: 'hidden',
        height: '100%',
        width: '100%'
      }}
    >
      <FactsheetHeader factsheet={factsheetDetails} />
      <FactsheetBody
        factsheet={factsheetDetails}
        openNewTab={openNewTab}
      />
    </Box>
  );
};

export default FactsheetWindow;
